/**
 * SqlProceduresTab — Ustawienia → „Procedury SQL": jedna sekcja z wewnętrznymi zakładkami
 * „Kartoteki" (procedury kartotek: kontrola przed zapisem, po zapisie, kontrola przed usunięciem
 * i przycisk SQL na formularzu rekordu, `RecordProceduresTab`) i „Procesy" (procedury kontrolne
 * przejść procesu, `ProcessProceduresTab`). Treść zakładek bez zmian — ta obudowa tylko je zestawia.
 *
 * Każda zakładka widoczna wg swojej dotychczasowej bramki (`useSettingsSectionAccess`); samą
 * sekcję Ustawienia pokazują, gdy user ma którąkolwiek z nich (`canManageSqlProcedures`).
 *
 * Aktywna zakładka żyje w `?tab=records|processes` (deep-link; stare adresy sekcji
 * `/settings/record-procedures` i `/settings/process-procedures` przekierowują tutaj). Powłoka
 * kart liczy kartę po samej ścieżce, więc zmiana parametru nie otwiera nowej karty. Brak,
 * nieznana albo niedostępna wartość → pierwsza widoczna zakładka.
 */

import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ProcessProceduresTab } from '@/components/processes/ProcessProceduresTab';
import { Tabs, type TabsItem } from '@/components/Tabs';
import { useSettingsSectionAccess } from '@/hooks/useSettingsSectionAccess';

import { RecordProceduresTab } from './RecordProceduresTab';

export type SqlProceduresTabId = 'records' | 'processes';

export function SqlProceduresTab() {
  const { t } = useTranslation();
  const { canManageRecordProcedures, canManageProcessProcedures } = useSettingsSectionAccess();
  const [searchParams, setSearchParams] = useSearchParams();

  const items: TabsItem[] = [
    ...(canManageRecordProcedures
      ? [
          {
            value: 'records' satisfies SqlProceduresTabId,
            label: t('sqlProcedures.tabs.records'),
            content: <RecordProceduresTab />,
            testId: 'sql-procedures-tab-records',
          },
        ]
      : []),
    ...(canManageProcessProcedures
      ? [
          {
            value: 'processes' satisfies SqlProceduresTabId,
            label: t('sqlProcedures.tabs.processes'),
            content: <ProcessProceduresTab />,
            testId: 'sql-procedures-tab-processes',
          },
        ]
      : []),
  ];

  const requested = searchParams.get('tab');
  const active = items.find((i) => i.value === requested)?.value ?? items[0]?.value;

  if (!active) return null;

  return (
    <div data-testid="sql-procedures">
      <Tabs
        idPrefix="sql-procedures"
        items={items}
        value={active}
        onChange={(next) => {
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              params.set('tab', next);
              return params;
            },
            { replace: true },
          );
        }}
      />
    </div>
  );
}
